import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { ALLOW_ANONYMOUS, DA_SERVICE_TOKEN, ITokenModel } from '@delon/auth';
import type { AlainAuthConfig } from '@delon/util/config';
import { throwError } from 'rxjs';

import { ApplicationLogic } from '../logic/application.logic';

/**
 * 为请求补充项目约定的 Token，并在无 Token 时进入当前角色登录流程。
 *
 * 入参：Angular 请求对象和后续请求处理函数。
 * 出参：继续处理后的请求 Observable，或无认证信息时的 401 Observable。
 * 示例：普通业务请求会携带 `token` 请求头，登录请求通过 `ALLOW_ANONYMOUS` 跳过认证。
 */
export const roleAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenSrv = inject(DA_SERVICE_TOKEN);
  const options = tokenSrv.options;

  if (isAnonymousRequest(req, options)) {
    return next(req);
  }

  const tokenData = tokenSrv.get() as ITokenModel | null;
  if (tokenData?.token) {
    return next(withToken(req, tokenData, options));
  }

  ApplicationLogic.getInstance(inject(Injector)).toLogin();
  return throwError(
    () =>
      new HttpErrorResponse({
        url: req.url,
        headers: req.headers,
        status: 401,
        statusText: '未授权'
      })
  );
};

/**
 * 判断请求是否明确允许匿名访问。
 *
 * 入参：请求对象和认证配置。
 * 出参：请求允许匿名访问时返回 `true`。
 * 示例：登录接口设置 `ALLOW_ANONYMOUS` 后返回 `true`。
 */
function isAnonymousRequest(req: HttpRequest<unknown>, options: AlainAuthConfig): boolean {
  if (req.context.get(ALLOW_ANONYMOUS)) return true;
  return options.ignores?.some(ignore => ignore.test(req.url)) ?? false;
}

/**
 * 按认证配置把 Token 写入请求头、请求体或查询参数。
 *
 * 入参：原请求、Token 数据和认证配置。
 * 出参：携带 Token 的新请求，不修改原请求。
 * 示例：`token_send_place` 为 `header` 时写入 `token: <value>`。
 */
function withToken(req: HttpRequest<unknown>, tokenData: ITokenModel, options: AlainAuthConfig): HttpRequest<unknown> {
  const tokenKey = options.token_send_key || 'token';
  const tokenTemplate = options.token_send_template || '${token}';
  const tokenValue = tokenTemplate.replace(/\$\{([\w]+)\}/g, (_, key: string) => String(tokenData[key] ?? ''));

  switch (options.token_send_place) {
    case 'body': {
      const body =
        req.body && typeof req.body === 'object' && !(req.body instanceof FormData) ? { ...(req.body as Record<string, unknown>) } : {};
      body[tokenKey] = tokenValue;
      return req.clone({ body });
    }
    case 'url':
      return req.clone({ params: req.params.set(tokenKey, tokenValue) });
    default:
      return req.clone({ setHeaders: { [tokenKey]: tokenValue } });
  }
}
